import { useEffect, useState } from "react";
import { Button, Modal } from "react-bootstrap";
import DataTable from "react-data-table-component";
import { Plus, PencilSquare, Trash, Eye } from "react-bootstrap-icons";
import Swal from 'sweetalert2';
import { useNavigate } from "react-router-dom";
import RateReview from "../components/RateReview";
import api from "../api/axios";

export default function RatePage() {
    const [search, setSearch] = useState("");
    const [filteredRates, setFilteredRates] = useState([]);
    const [rates, setRates] = useState([]);
    const [rateDelete, setRateDelete] = useState(null)
    const [loadingTable, setLoadingTable] = useState(false);
    const [loadingSave, setLoadingSave] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [showReviewModal, setShowReviewModal] = useState(false);
    const [selectedRate, setSelectedRate] = useState(null);
    const navigate = useNavigate();
  
    const normalize = (txt) =>
        String(txt ?? "")
            .toLowerCase()
            .normalize("NFD")
            .replace(/[\u0300-\u036f]/g, "");

    const formatDate = (value) => {
        if (!value) return "-";

        const [year, month, day] = value.split("T")[0].split("-");
        return `${day}/${month}/${year}`;
    };


    const formatStatus = (status) =>
        status === "draft" ? "borrador" : "publicado";

    const getSearchableText = (row) => {
        return normalize(`
            ${row.year}-${row.number} ${row.name}
            ${formatDate(row.issued_at)}
            ${formatDate(row.effective_from)} hasta ${formatDate(row.effective_to)}
            ${formatStatus(row.status)}
        `);
    };

    // ==============================
    // Fetch inicial
    // ==============================
    useEffect(() => {
        fetchRates();
    }, []);

    const fetchRates = async () => {
        try {
            setLoadingTable(true);
            const res = await api.get("/rates/list");
            console.log(res.data.data);
            setFilteredRates(res.data.data);
            setRates(res.data.data);
        } catch (err) {
            console.error(err);
        } finally {
            setLoadingTable(false);
        }
    }

    useEffect(() => {
        const term = normalize(search);

        if (!term) {
            setFilteredRates(rates);
            return;
        }

        const result = rates.filter((row) =>
            getSearchableText(row).includes(term)
        );

        setFilteredRates(result);
    }, [search, rates]);

    const handleShowEdit = (r) => {
        if (r.status === "published") {
            setSelectedRate(r);
            setShowReviewModal(true);
        } else {
            navigate(`/rates/${r.id}/wizard`);
        }
    }

    const handleShowNew = () => {
        navigate(`/rates/new`);
    }

    const handleShowDelete = (rate) => {
        setShowDeleteModal(true);
        setRateDelete(rate.id);
    }

    // ==============================
    // Eliminar
    // ==============================
    const handleDelete = async () => {
        setLoadingSave(true);
        try {
            await api.delete(`/rates/${rateDelete}`);
            setShowDeleteModal(false);
            fetchRates();
        } catch (err) {
            
            Swal.fire({
                title: 'Error',
                text: "Error desconocido al eliminar",
                icon: 'error',
                timer: 1500,
                showConfirmButton: false
            });
            
        } finally {
            setLoadingSave(false);
        }
    };

    // ==============================
    // Columnas tabla
    // ==============================
    const columns = [
        { name: "Acuerdo", selector: (row) => `${row.year}-${row.number} ${row.name}`, sortable: true },
        { name: "Fecha expedición", selector: (row) => formatDate(row.issued_at), sortable: true, sortFunction: (a, b) => new Date(a.issued_at) - new Date(b.issued_at) },
        { name: "Vigencia", selector: (row) => `${formatDate(row.effective_from)} - ${row.effective_to ? formatDate(row.effective_to) : 'Sin fecha fin'}`, sortable: true },
        {
            name: "Estado",
            cell: (row) => (
                <span
                className={`badge ${
                    row.status === "draft" ? "bg-secondary" : "bg-success"
                }`}
                >
                {row.status === "draft" ? "Borrador" : "Publicado"}
                </span>
            ),
            sortable: true,
            },
        {
        name: "Opciones",
        cell: (row) => (
            <div className="d-flex gap-2">
                <Button
                    variant="outline-info"
                    size="sm"
                    onClick={() => handleShowEdit(row)}
                >
                    {row.status === 'draft' ? (
                        <PencilSquare />
                    ) : (
                        <Eye />
                    )}
                </Button>
                {row.status === 'draft' && (
                    <Button
                        variant="outline-danger"
                        size="sm"
                        onClick={() => handleShowDelete(row)}
                    >
                        <Trash />
                    </Button>
                )}
            </div>
        ),
        ignoreRowClick: true,
        },
    ];

    return (
        <div className="mt-4">
            {/* TABLA */}
            <DataTable
                title="Decretos Tarifarios"
                columns={columns}
                data={filteredRates}
                pagination
                highlightOnHover
                striped
                progressPending={loadingTable}
                progressComponent={
                    <div className="text-center p-3">
                        <div className="spinner-border text-primary"></div>
                        <p>Cargando...</p>
                    </div>
                }
                subHeader
                subHeaderComponent={
                <div className="d-flex justify-content-between w-100">
                    <Button
                        variant="outline-success"
                        size="sm" 
                        onClick={handleShowNew}
                    >
                        Nuevo acuerdo <Plus />
                    </Button>

                    <input
                        type="text"
                        className="form-control w-25"
                        placeholder="Buscar..."
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                    />
                </div>
                }
            />

            {/* MODAL DELETE */}
            <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)}>
                <Modal.Header closeButton>
                    <Modal.Title>Eliminación de borrador acuerdo</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p className="text-muted">
                        Al borrar el acuerdo no se podrá recuperar el progreso.
                    </p>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="danger" onClick={handleDelete} disabled={loadingSave}>
                    {loadingSave ? (
                        <>
                        <span className="spinner-border spinner-border-sm me-2"></span>
                        Cargando...
                        </>
                    ) :  (
                        "Eliminar"
                    )}
                    </Button>
                </Modal.Footer>
            </Modal>

            <Modal
                show={showReviewModal}
                onHide={() => setShowReviewModal(false)}
                size="xl"
                backdrop="static"
            >
                <Modal.Header closeButton>
                    <Modal.Title>
                        Información del acuerdo tarifario
                    </Modal.Title>
                </Modal.Header>

                <Modal.Body>
                    {selectedRate && (
                    <RateReview
                        rate={selectedRate}
                        iseditable={false}
                    />
                    )}
                </Modal.Body>
            </Modal>
        </div>
    );
}